import { io } from 'socket.io-client';
import { getActiveAccountToken } from '../utils/accountStorage';

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || '';

class SocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
  }

  connect() {
    // Already connected — nothing to do
    if (this.socket?.connected) return;

    // Socket exists but is still connecting/reconnecting — don't create another
    if (this.socket && this.socket.active) return;

    // Stale socket that gave up — clean up and recreate
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    const token = getActiveAccountToken();
    this.socket = io(SOCKET_URL, {
      withCredentials: true,
      auth: token ? { token } : {},
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 10000,
    });

    this.socket.on('connect', () => {
      console.log('[socket] connected id=', this.socket?.id);
    });
    this.socket.on('disconnect', (reason) => {
      console.log('[socket] disconnected:', reason);
    });
    this.socket.on('connect_error', (err) => {
      console.error('[socket] connect_error:', err.message);
    });

    // Re-register any user listeners that were added before socket existed
    this.listeners.forEach((callbacks, event) => {
      callbacks.forEach((cb) => this.socket.on(event, cb));
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
  }

  /**
   * Register a listener. If the socket is already connected and event is
   * 'connect', the callback fires immediately so callers never miss it
   * (critical for React StrictMode double-invoke).
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);

    if (this.socket) {
      this.socket.on(event, callback);
      // Fire immediately if already connected — avoids race where the
      // 'connect' event fired before this listener was registered
      if (event === 'connect' && this.socket.connected) {
        callback();
      }
    }
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }
    if (this.socket) {
      this.socket.off(event, callback);
    }
  }

  emit(event, data) {
    if (this.socket?.connected) {
      this.socket.emit(event, data);
    }
  }

  joinConsultation(consultationId) { this.emit('join:consultation', consultationId); }
  leaveConsultation(consultationId) { this.emit('leave:consultation', consultationId); }
  emitTaskUpdate(data) { this.emit('task:update', data); }
  emitNewMessage(data) { this.emit('message:new', data); }

  get connected() {
    return this.socket?.connected ?? false;
  }
}

const socketService = new SocketService();
export default socketService;

